import React, { useContext, useCallback } from 'react';
import { useSocketEvent } from '../context/SocketContext';
import { AppContext } from '../window';
import { SocketEvent } from './unifiedSocket';
import { getLastTransaction } from '../api/userApi';
import { usePaymentSuccess } from '../context/PaymentSuccessContext';
import { fetchSession, handleSessionLost, isMemberSession } from '../api/session';
import { useResync } from './resync';

export interface WalletUpdateEvent {
  credits: number;
  freeMessages?: number;
  transaction: {
    amount: number;
    currencyIso: string;
    description: string;
  };
}

const UserSocketManager: React.FC = () => {
  const { setUser } = useContext(AppContext);
  const { showPaymentSuccess } = usePaymentSuccess();

  // Handle wallet update events
  const handleWalletUpdate = useCallback(async (event: SocketEvent<WalletUpdateEvent>) => {
    const { credits, freeMessages, transaction } = event.data;
    
    // Update the user's wallet credits in app context
    setUser(prevUser => 
      prevUser ? {
        ...prevUser,
        wallet: {
          ...prevUser.wallet,
          credits: credits,
          // Update freeMessages if provided
          ...(freeMessages !== undefined && { freeMessages })
        }
      } : prevUser
    );

    // Show payment success modal instead of notification
    showPaymentSuccess(transaction.amount, 'success');

    // Fetch the updated lastTransaction
    try {
      const response = await getLastTransaction();
      if (response.success && response.lastTransaction) {
        // Update the user's credentials with the new lastTransaction
        setUser(prevUser => 
          prevUser ? {
            ...prevUser,
            credentials: {
              ...prevUser.credentials,
              lastTransaction: response.lastTransaction ?? undefined
            }
          } : prevUser
        );
      }
    } catch (error) {
      console.error('Failed to update lastTransaction after wallet update:', error);
    }
  }, [setUser, showPaymentSuccess]);

  // Subscribe to wallet update events
  useSocketEvent<WalletUpdateEvent>('event/wallet-update', handleWalletUpdate);

  // After a connection gap re-read wallet and unread count (credits pushed after
  // a payment may have been missed); a session that is gone sends to login
  useResync(async () => {
    const session = await fetchSession();
    if (!session) {
      return;
    }
    if (session.user === null) {
      if (isMemberSession()) {
        handleSessionLost();
      }
      return;
    }
    const { wallet, unreadMessageCount, coupon } = session.user;
    setUser(prevUser =>
      prevUser ? { ...prevUser, wallet, unreadMessageCount, coupon } : prevUser
    );
  });

  // This component doesn't render anything
  return null;
};

export default UserSocketManager;